import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { QRCodeSVG } from 'qrcode.react'
import { Col, Modal, Row } from 'components'
import {
    buildEnrollmentQrUrl,
    constants,
    findPermission,
    getStorage
} from 'helpers'
import bindalIcon from '../../../assets/bindal-icon.png'
import qrIcon from '../../../assets/enrollment-qr-icon.svg'
import './villageEnrollmentQr.scss'

type EnrollmentVillage = {
    code?: string
    name?: string
}

type VillageEnrollmentQrProps = {
    villageCode?: string
    villageName?: string
    villages?: EnrollmentVillage[]
    className?: string
}

export const VillageEnrollmentQr = ({
    villageCode,
    villageName,
    villages,
    className
}: VillageEnrollmentQrProps) => {
    const { t } = useTranslation()
    const [showQrModal, setShowQrModal] = useState(false)
    const [showVillagePicker, setShowVillagePicker] = useState(false)
    const [selectedVillage, setSelectedVillage] = useState<EnrollmentVillage>()
    const userPermissions = getStorage(constants.USER_PERMISSIONS)
    const canViewEnrollmentQr = findPermission(
        userPermissions,
        constants.CAN_VIEW_ENROLLMENT_QR
    )

    const candidateVillages = villages?.length
        ? villages
        : [{ code: villageCode, name: villageName }]
    const villagesWithCode = candidateVillages.filter((village) => village.code)

    const activeVillage = selectedVillage || villagesWithCode[0]
    const enrollmentUrl = buildEnrollmentQrUrl(activeVillage?.code)

    if (!canViewEnrollmentQr || !villagesWithCode.length || !enrollmentUrl) {
        return null
    }

    const openEnrollmentQr = () => {
        if (villagesWithCode.length === 1) {
            setSelectedVillage(villagesWithCode[0])
            setShowQrModal(true)
            return
        }
        setShowVillagePicker(true)
    }

    const selectVillage = (village: EnrollmentVillage) => {
        setSelectedVillage(village)
        setShowVillagePicker(false)
        setShowQrModal(true)
    }

    const closeQrModal = () => setShowQrModal(false)
    const isCompact = className?.includes('enrollment-qr-home--compact')

    return (
        <>
            <Row
                className={
                    isCompact
                        ? 'justify-content-center enrollment-qr-home-row--compact'
                        : 'justify-content-center'
                }
            >
                <Col
                    md={12}
                    className={['enrollment-qr-home', className]
                        .filter(Boolean)
                        .join(' ')}
                >
                    <button
                        type="button"
                        className="enrollment-qr-btn"
                        aria-label={t('village.enrollmentQr')}
                        data-testid="enrollment-qr-btn"
                        id="enrollment-qr-btn"
                        onClick={openEnrollmentQr}
                    >
                        <span className="enrollment-qr-btn__icon">
                            <img
                                src={bindalIcon}
                                alt=""
                                width={50}
                                height={50}
                            />
                        </span>
                        <span className="enrollment-qr-btn__icon">
                            <img src={qrIcon} alt="" width={50} height={50} />
                        </span>
                    </button>
                </Col>
            </Row>
            <Modal
                showModal={showVillagePicker}
                position="bottom"
                hideButtons
                showCloseIconBtn
                closeClickHandler={() => setShowVillagePicker(false)}
                textHeader={t('village.chooseVillage')}
                testId="enrollment-qr-village-picker"
            >
                <div className="enrollment-qr--village-list">
                    {villagesWithCode.map((village) => (
                        <button
                            key={village.code}
                            type="button"
                            className="enrollment-qr--village-option"
                            onClick={() => selectVillage(village)}
                        >
                            {village.name || village.code}
                        </button>
                    ))}
                </div>
            </Modal>
            <Modal
                showModal={showQrModal}
                position="fullscreen"
                hideButtons
                showCloseIconBtn
                closeClickHandler={closeQrModal}
                testId="enrollment-qr-modal"
                className="enrollment-qr-modal"
            >
                <div className="enrollment-qr--content">
                    {activeVillage?.name && (
                        <h2 className="enrollment-qr--village">
                            {activeVillage.name}
                        </h2>
                    )}
                    <div
                        className="enrollment-qr--code"
                        data-testid="enrollment-qr-code"
                    >
                        <QRCodeSVG value={enrollmentUrl} size={280} />
                    </div>
                </div>
            </Modal>
        </>
    )
}
